import { ReactNode } from 'react'
import { useMount } from 'react-use'
import { useRecoilState } from 'recoil'
import { v4 as uuidv4 } from 'uuid'

import { Box, ListItem, MenuListItemProps } from '@cutover/react-ui'
import { usePostAppEvents } from '../apps-api'
import { AppsComponentParser } from '../apps-component-parser'
import { AppComponentNodeProps, ContentNode, ContentNodeOption } from '../apps-types'
import { useComponentPropsAndState } from '../apps-state'
import { listItemOpenWithContextAndId } from 'main/recoil/apps/selectors'

type ListItemNodeProps = AppComponentNodeProps & {
  children: ReactNode
  title?: string
  subTitle?: string
  // 'small' size is prohibited as layout is too cramped in presence of subtitle. Revisit when list-item component is refactored.
  size?: 'medium' | 'large'
  leftContent?: ContentNode[]
  rightContent?: ContentNode[]
  menuOptions?: ContentNodeOption[]
  expandable?: boolean
  defaultOpen?: boolean
  error?: boolean
}

export const ListItemNode = ({ appId, resourceId, id, ...props }: ListItemNodeProps) => {
  const { componentProps, state } = useComponentPropsAndState(appId, resourceId, id, props)
  const {
    children,
    title,
    subTitle,
    size,
    rightContent,
    leftContent,
    menuOptions,
    expandable = false,
    defaultOpen = false,
    error = false
  } = componentProps as ListItemNodeProps

  const context = `${resourceId}-${appId}`
  const postAppEvents = usePostAppEvents()
  const [listItemOpenState, setListItemOpenState] = useRecoilState(
    listItemOpenWithContextAndId({ context, id: id || '' })
  )

  const endComponents: ReactNode[] | undefined =
    rightContent &&
    rightContent.map(node => <AppsComponentParser content={[node]} appId={appId} resourceId={resourceId} />)

  const startComponents: ReactNode[] | undefined =
    leftContent &&
    leftContent.map(node => {
      return (
        <AppsComponentParser content={[node]} appId={appId} resourceId={resourceId} key={`${node.type}-${uuidv4()}`} />
      )
    })

  const openInNewTab = (href: string) => {
    window.open(href, '_blank', 'noreferrer')
  }

  const handleMenuOptionClick = (option: MenuListItemProps) => {
    const selectedOption = menuOptions?.find(opt => opt.label === option.value)
    if (selectedOption) {
      if (selectedOption.href) {
        openInNewTab(selectedOption.href)
      }
      const payload = {
        app_id: appId,
        runbook_id: resourceId,
        events: [selectedOption],
        state
      }
      postAppEvents(payload)
    }
  }

  const clickableMenuOptions = menuOptions?.map(option => {
    return {
      ...option,
      onClick: handleMenuOptionClick
    }
  })

  const toggleListItemOpenState = () => {
    const open = listItemOpenState ?? false
    setListItemOpenState(!open)
  }

  useMount(() => {
    if (listItemOpenState === undefined) {
      setListItemOpenState(defaultOpen)
    }
  })

  return (
    <ListItem
      hasError={error}
      title={title}
      open={listItemOpenState ?? defaultOpen}
      onClick={expandable ? toggleListItemOpenState : undefined}
      prominence="high"
      subTitle={subTitle}
      menuOptions={clickableMenuOptions as unknown as MenuListItemProps[]}
      size={size}
      expandable={expandable}
      expandableContent={children}
      startComponent={
        <Box direction="row" align="baseline" gap="xsmall">
          {startComponents}
        </Box>
      }
      endComponents={endComponents}
    >
      {!expandable && children}
    </ListItem>
  )
}
